import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Condominium } from '../types';
import { supabase } from '../integrations/supabase/client';

interface CondominiumContextType {
  condominiums: Condominium[];
  addCondominium: (condominium: Condominium) => void;
  updateCondominium: (condominium: Condominium) => void;
  deleteCondominium: (id: string) => void;
  getCondominiumById: (id: string) => Condominium | undefined;
}

const CondominiumContext = createContext<CondominiumContextType | undefined>(undefined);

export const CondominiumProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [condominiums, setCondominiums] = useState<Condominium[]>([]);

  // Carregar condomínios do Supabase
  useEffect(() => {
    const fetchCondominiums = async () => {
      try {
        const { data, error } = await supabase
          .from('condominiums')
          .select('*')
          .eq('is_active', true)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Erro ao carregar condomínios:', error);
          return;
        }

        // Converter dados do Supabase para o formato Condominium
        const convertedCondominiums: Condominium[] = data?.map(cond => ({
          id: cond.id,
          name: cond.name,
          address: cond.address as any,
          features: cond.amenities || [],
          images: Array.isArray(cond.images) ? cond.images.map((img: any) => ({
            id: img.id,
            url: img.url,
            name: img.name
          })) : [],
          createdAt: new Date(cond.created_at),
          updatedAt: new Date(cond.updated_at)
        })) || [];

        setCondominiums(convertedCondominiums);
      } catch (error) {
        console.error('Erro ao buscar condomínios:', error);
      }
    };

    fetchCondominiums();
  }, []);

  const uploadImages = async (images: any[], condominiumId: string) => {
    const uploadedImages = [];
    
    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      if (image.file) {
        const fileExt = image.file.name.split('.').pop();
        const fileName = `${condominiumId}/${Date.now()}_${i}.${fileExt}`;
        
        const { error } = await supabase.storage
          .from('condominium-images')
          .upload(fileName, image.file);
          
        if (error) {
          console.error('Erro ao fazer upload da imagem:', error);
          continue;
        }
        
        const { data: urlData } = supabase.storage
          .from('condominium-images')
          .getPublicUrl(fileName);
          
        uploadedImages.push({
          id: image.id,
          url: urlData.publicUrl,
          name: image.name
        });
      } else if (image.url) {
        // Imagem já existe no storage
        uploadedImages.push({
          id: image.id,
          url: image.url,
          name: image.name
        });
      }
    }
    
    return uploadedImages;
  };

  const addCondominium = async (condominium: Condominium) => {
    try {
      // Primeiro salvar o condomínio básico
      const { data, error } = await supabase
        .from('condominiums')
        .insert({
          name: condominium.name,
          address: condominium.address,
          amenities: condominium.features,
          is_active: true
        })
        .select()
        .single();

      if (error) {
        console.error('Erro ao adicionar condomínio:', error);
        throw error;
      }

      let uploadedImages: any[] = [];
      
      // Fazer upload das imagens se houver
      if (condominium.images && condominium.images.length > 0) {
        uploadedImages = await uploadImages(condominium.images, data.id);
        
        // Atualizar o condomínio com as URLs das imagens
        await supabase
          .from('condominiums')
          .update({ images: uploadedImages })
          .eq('id', data.id);
      }

      const newCondominium: Condominium = {
        id: data.id,
        name: data.name,
        address: data.address as any,
        features: data.amenities || [],
        images: uploadedImages,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at)
      };

      setCondominiums(prev => [...prev, newCondominium]);
    } catch (error) {
      console.error('Erro ao adicionar condomínio:', error);
      throw error;
    }
  };

  const updateCondominium = async (updatedCondominium: Condominium) => {
    try {
      let uploadedImages: any[] = [];
      
      // Fazer upload das novas imagens se houver
      if (updatedCondominium.images && updatedCondominium.images.length > 0) {
        uploadedImages = await uploadImages(updatedCondominium.images, updatedCondominium.id);
      }

      const { error } = await supabase
        .from('condominiums')
        .update({
          name: updatedCondominium.name,
          address: updatedCondominium.address,
          amenities: updatedCondominium.features,
          images: uploadedImages,
          updated_at: new Date().toISOString()
        })
        .eq('id', updatedCondominium.id);

      if (error) {
        console.error('Erro ao atualizar condomínio:', error);
        throw error;
      }

      const updatedCondominiumWithImages: Condominium = {
        ...updatedCondominium,
        images: uploadedImages,
        updatedAt: new Date()
      };

      setCondominiums(prev => 
        prev.map(condominium => 
          condominium.id === updatedCondominium.id 
            ? updatedCondominiumWithImages
            : condominium
        )
      );
    } catch (error) {
      console.error('Erro ao atualizar condomínio:', error);
      throw error;
    }
  };

  const deleteCondominium = async (id: string) => {
    try {
      const { error } = await supabase
        .from('condominiums')
        .update({ is_active: false })
        .eq('id', id);

      if (error) {
        console.error('Erro ao deletar condomínio:', error);
        throw error;
      }

      setCondominiums(prev => prev.filter(condominium => condominium.id !== id));
    } catch (error) {
      console.error('Erro ao deletar condomínio:', error);
      throw error;
    }
  };

  const getCondominiumById = (id: string) => {
    return condominiums.find(condominium => condominium.id === id);
  };

  const value = {
    condominiums,
    addCondominium,
    updateCondominium,
    deleteCondominium,
    getCondominiumById
  };

  return (
    <CondominiumContext.Provider value={value}>
      {children}
    </CondominiumContext.Provider>
  );
};

export const useCondominium = () => {
  const context = useContext(CondominiumContext);
  if (context === undefined) {
    throw new Error('useCondominium must be used within a CondominiumProvider');
  }
  return context;
};